import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError, qs } from './api.ts';
import type { Booking, Busy, Me, Room } from './types.ts';

export function useMe() {
  return useQuery({
    queryKey: ['me'],
    queryFn: async () => {
      try {
        return await api.get<Me>('/api/me');
      } catch (e) {
        if (e instanceof ApiError && e.status === 401) return null;
        throw e;
      }
    },
    staleTime: 60_000,
  });
}

export function useRooms() {
  return useQuery({
    queryKey: ['rooms'],
    queryFn: () => api.get<Room[]>('/api/rooms'),
    staleTime: 60_000,
  });
}

export function useAvailability(from: Date, to: Date) {
  return useQuery({
    queryKey: ['availability', from.toISOString(), to.toISOString()],
    queryFn: () =>
      api.get<Busy[]>(
        `/api/availability?${qs({ from: from.toISOString(), to: to.toISOString() })}`,
      ),
    refetchInterval: 60_000,
  });
}

export function useMyBookings(past = false) {
  return useQuery({
    queryKey: ['mine', past],
    queryFn: () => api.get<Booking[]>(`/api/bookings/mine${past ? '?past=1' : ''}`),
  });
}

/** After any booking change: refresh calendars and lists. */
export function useRefreshBookings() {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: ['availability'] });
    void qc.invalidateQueries({ queryKey: ['mine'] });
    void qc.invalidateQueries({ queryKey: ['admin-bookings'] });
    void qc.invalidateQueries({ queryKey: ['notifications'] });
  };
}
